import React, { useState, useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { linkProps } from '../../utils/appLink';
import client from '../../utils/contentfulClient';
import ScrollFadeWrapper from '../ScrollFadeWrapper/ScrollFadeWrapper';
import LinkWithIconComponent from '../HomePage/LinkWithIcon/LinkWithIconComponent';
import Image from '../Image/Image';
import {
  getContentfulImageUrl,
  getContentfulSrcSet,
} from '../../utils/contentfulImage';

const formatBlogDate = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

const normalizeSlug = (slug) => slug.replace(/\/$/, '');

const getSlugValue = (entry) => (Array.isArray(entry) ? entry[0] : entry);
const getTitleOverride = (entry) =>
  Array.isArray(entry) ? entry[1] : undefined;

const mapPostToCard = (post, titleOverride) => {
  const { slug, category, heading, blogDate, headingImage } = post.fields;
  const cleanSlug = normalizeSlug(slug);
  const link = cleanSlug.includes('/')
    ? `/blog/${slug}`
    : `/blog/${category}/${slug}`;
  return {
    image: headingImage?.fields?.file?.url
      ? `https:${headingImage.fields.file.url}`
      : '',
    date: formatBlogDate(blogDate),
    title: titleOverride || heading,
    link,
  };
};

const VerticalSeoSubjectMobileGuideComponent = ({ data }) => {
  const {
    title,
    description,
    footer,
    footerLink,
    footerLinl,
    slugs = [],
  } = data;
  const [cards, setCards] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const resolvedFooterLink = footerLink || footerLinl;

  useEffect(() => {
    if (!slugs.length) {
      setCards([]);
      setHasLoaded(true);
      return;
    }

    const fetchCards = async () => {
      try {
        const slugVariants = Array.from(
          new Set(
            slugs.flatMap((entry) => {
              const s = getSlugValue(entry);
              return [normalizeSlug(s), `${normalizeSlug(s)}/`];
            })
          )
        );

        const response = await client.getEntries({
          content_type: 'blogPost',
          'fields.slug[in]': slugVariants.join(','),
        });

        const bySlug = new Map(
          response.items.map((post) => [normalizeSlug(post.fields.slug), post])
        );

        const ordered = slugs
          .map((entry) => ({
            post: bySlug.get(normalizeSlug(getSlugValue(entry))),
            titleOverride: getTitleOverride(entry),
          }))
          .filter(({ post }) => Boolean(post))
          .map(({ post, titleOverride }) => mapPostToCard(post, titleOverride));

        setCards(ordered);
      } catch (err) {
        // ignore
      } finally {
        setHasLoaded(true);
      }
    };

    fetchCards();
  }, [slugs]);

  if (hasLoaded && cards.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '40px',
      }}
    >
      {/* Title and Description */}
      <Box sx={{ textAlign: 'center' }}>
        <ScrollFadeWrapper variant="text">
          <Typography
            component={'h3'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '2rem',
              lineHeight: '2.25rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              mb: '16px',
            }}
          >
            {title}
          </Typography>
        </ScrollFadeWrapper>

        <ScrollFadeWrapper variant="text" delay={0.2}>
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '1.25rem',
              lineHeight: '1.5rem',
              letterSpacing: '-0.03em',
              color: '#162F3B',
              maxWidth: '344px',
              mx: 'auto',
            }}
          >
            {description}
          </Typography>
        </ScrollFadeWrapper>
      </Box>

      {/* Blog Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {cards.map((card, index) => (
          <ScrollFadeWrapper key={index} delay={index * 0.1}>
            <Box
              sx={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '34px',
                boxShadow:
                  '0px 6px 16px 0px #162F3B1F, 0px 1px 2px 0px #A2ACB11F',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Image */}
              <Image
                width={358}
                height={220}
                src={getContentfulImageUrl(card.image, {
                  width: 358,
                  height: 220,
                })}
                srcSet={getContentfulSrcSet(card.image, {
                  width: 358,
                  height: 220,
                })}
                alt={card.title}
                sx={{
                  width: '100%',
                  height: '220px',
                  objectFit: 'cover',
                  backgroundColor: '#A2ACB1',
                }}
              />

              {/* Card Content */}
              <Box
                sx={{
                  padding: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  flexGrow: 1,
                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  <Typography
                    component={'p'}
                    sx={{
                      fontFamily: 'Overline, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      lineHeight: '1.25rem',
                      textAlign: 'right',
                      color: '#162F3B',
                    }}
                  >
                    {card.date}
                  </Typography>

                  <Typography
                    component={'h6'}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.03em',
                      color: '#162F3B',
                      textAlign: 'left',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Link
                    component={'a'}
                    {...linkProps(card.link)}
                    underline="none"
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.02em',
                      color: '#162F3B',
                      '&:hover': {
                        color: '#9EA900',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Read More
                  </Link>
                </Box>
              </Box>
            </Box>
          </ScrollFadeWrapper>
        ))}
      </Box>

      {/* Footer */}
      {footer &&
        (resolvedFooterLink ? (
          <LinkWithIconComponent
            href={resolvedFooterLink}
            iconSize="1rem"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5rem',
              letterSpacing: '-0.02em',
              color: '#162F3B',
              textAlign: 'center',
              maxWidth: '343px',
              '&:hover': {
                color: '#9EA900',
              },
            }}
          >
            {footer}
          </LinkWithIconComponent>
        ) : (
          <Typography
            component={'p'}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.5rem',
              letterSpacing: '-0.02em',
              color: '#162F3B',
              textAlign: 'center',
              maxWidth: '343px',
            }}
          >
            {footer}
          </Typography>
        ))}
    </Box>
  );
};

export default VerticalSeoSubjectMobileGuideComponent;
